/**
 * Native return-from-Stripe refresh (D51f Phase 5, FR-P15, OQ-41).
 *
 * On iOS/Android, checkout and the customer portal open in a browser layer
 * (`externalPurchase.ts`), so there is no in-app redirect to observe. A success
 * may or may not also fire a deep link (`useDeepLinkHandler`), and an abandoned
 * or interrupted checkout fires nothing at all — yet the member must not be
 * left on a stale "still free" cache.
 *
 * So: `externalPurchase.ts` sets `subscription.checkoutAttempted` whenever it
 * opens a hosted Stripe page, and this hook runs the ONE convergence point
 * (`useInvalidateSubscription()`), once, on the first of two "the member is
 * back" signals:
 * - `App`'s `appStateChange` with `isActive === true` (Android, and iOS when
 *   the app itself was backgrounded), and
 * - `Browser`'s `browserFinished` (the member closed the browser layer).
 * Both share one handler that clears the flag BEFORE starting the refresh, so
 * when both arrive (Android usually fires both) it runs once in total, and a
 * second event while the poll is running cannot re-trigger it. Every other
 * foreground is ignored, so entitlements are not refetched on each app switch.
 * When the trip was abandoned the refresh finds nothing changed and the
 * bounded poll simply runs out.
 *
 * Why two signals (read from the installed plugin sources, not exercised on a
 * device): iOS presents the browser in-process as an `SFSafariViewController`
 * (`@capacitor/browser` `Browser.swift`) while `appStateChange` there fires
 * only from `UIApplication.didBecomeActive` / `willResignActive`
 * (`@capacitor/app` `AppPlugin.swift`), so tapping Done on that sheet produces
 * no `appStateChange` at all; `browserFinished` is emitted on both platforms.
 * Whether a successful iOS checkout also fires the deep link is UNPROVEN:
 * `getCheckoutReturnUrl` (the helper that would build a OneLink return URL) has
 * no caller, and both real callers pass `window.location.origin`-based URLs to
 * the backend. So the browser-closed signal, not the deep link, is what covers
 * a native checkout that ends without one.
 *
 * Web: nothing to do (same-window redirect, handled by
 * `SubscriptionSettings`' return-param effect), so no listener is registered.
 */
import { useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { useStore } from 'react-redux';
import { useAppDispatch, type RootState } from '@/store/store';
import { clearCheckoutAttempted, selectCheckoutAttempted } from '@/store/slices/subscriptionSlice';
import { useInvalidateSubscription } from '@/api/queries/useSubscriptionQueries';

export function useCheckoutForegroundRefresh(): void {
  const store = useStore<RootState>();
  const dispatch = useAppDispatch();
  const refreshBilling = useInvalidateSubscription();
  // The listener is registered once; it reads the latest refresh function
  // through a ref rather than re-registering on every identity change.
  const refreshRef = useRef(refreshBilling);
  refreshRef.current = refreshBilling;

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    // One handler for both "the member is back" signals. Read the flag at event time (not from a render-time
    // selector) so a flag set after this effect ran is never missed.
    const onReturn = () => {
      if (!selectCheckoutAttempted(store.getState())) return;
      dispatch(clearCheckoutAttempted());
      void refreshRef.current();
    };

    const appListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) onReturn();
    });
    const browserListener = Browser.addListener('browserFinished', onReturn);

    // Removal is chained on the add promise, so a cleanup that runs before it resolves (StrictMode's mount ->
    // cleanup -> mount) still removes the right handle; a rejection of either promise is absorbed.
    return () => {
      for (const listener of [appListener, browserListener]) {
        void listener.then((handle) => handle.remove()).catch(() => {});
      }
    };
  }, [store, dispatch]);
}
